import { useEffect, useState, type RefObject } from 'react';

export const SCROLL_FLAG_PREFIX = 'eoi-section-scroll-';
export const SCROLL_FLAG_TTL_MS = 10_000;

export function useEOISectionScroll(
  investorId: string | undefined,
  investorUpdatedAt: string | undefined,
  updateMode: boolean,
  withdrawMode: boolean,
  rootRef: RefObject<HTMLDivElement | null>,
) {
  const [pendingScroll, setPendingScroll] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined' || !investorId) {
      return;
    }

    const key = `${SCROLL_FLAG_PREFIX}${investorId}`;
    const stored = window.localStorage.getItem(key);
    if (!stored) {
      return;
    }

    let shouldScroll = false;
    try {
      const parsed = JSON.parse(stored) as { ts?: number } | null;
      if (
        parsed?.ts &&
        typeof parsed.ts === 'number' &&
        Date.now() - parsed.ts <= SCROLL_FLAG_TTL_MS
      ) {
        shouldScroll = true;
      }
    } catch (err) {
      console.warn('Unable to parse EOI scroll flag', err);
    }

    window.localStorage.removeItem(key);

    if (shouldScroll) {
      setPendingScroll(true);
    }
  }, [investorId, investorUpdatedAt]);

  useEffect(() => {
    if (!pendingScroll) {
      return;
    }

    if (typeof window === 'undefined') {
      setPendingScroll(false);
      return;
    }

    if (updateMode || withdrawMode) {
      return;
    }

    let cancelled = false;
    let rafId: number | null = null;
    const maxAttempts = 40;

    const tryScroll = (attempt = 0) => {
      if (cancelled) {
        return;
      }

      const element = rootRef.current;
      if (element) {
        element.scrollIntoView({
          behavior: 'auto',
          block: 'start',
          inline: 'nearest',
        });
        setPendingScroll(false);
        return;
      }

      if (attempt >= maxAttempts) {
        setPendingScroll(false);
        return;
      }

      rafId = window.requestAnimationFrame(() => tryScroll(attempt + 1));
    };

    tryScroll();

    return () => {
      cancelled = true;
      if (rafId !== null) {
        cancelAnimationFrame(rafId);
      }
    };
  }, [pendingScroll, updateMode, withdrawMode, rootRef]);
}
